import React, { useEffect, useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import formula1Service from '../services/formula1Service'
import { Formula1Race, Formula1Prediction } from '../types/formula1'
import toast from 'react-hot-toast'

const POSITIONS = 10

function positionLabel(position: number): string {
  if (position === 1) return '1st'
  if (position === 2) return '2nd'
  if (position === 3) return '3rd'
  return `${position}th`
}

export interface Formula1PredictionFormProps {
  roomId: string
  raceId: string
  race: Formula1Race
  onSaved?: () => void
}

const Formula1PredictionForm: React.FC<Formula1PredictionFormProps> = ({
  roomId,
  raceId,
  race,
  onSaved,
}) => {
  const [prediction, setPrediction] = useState<Formula1Prediction | null>(null)
  const [positionToDriver, setPositionToDriver] = useState<Record<number, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0)

  const loadExistingPrediction = useMemo(
    () => async () => {
      if (!roomId || !raceId) return
      try {
        const pred = await formula1Service.getPrediction(roomId, raceId)
        if (pred && pred.predictedPositions) {
          setPrediction(pred)
          const positionMap: Record<number, string> = {}
          Object.entries(pred.predictedPositions || {}).forEach(([driver, position]) => {
            positionMap[Number(position)] = driver
          })
          setPositionToDriver(positionMap)
          setIsEditMode(true)
        }
      } catch (error: any) {
        if (error.response?.status !== 404) {
          console.error('Error loading prediction:', error)
        }
      }
    },
    [roomId, raceId]
  )

  useEffect(() => {
    setPrediction(null)
    setPositionToDriver({})
    setIsEditMode(false)
    setSelectedPositionIndex(0)
    loadExistingPrediction()
  }, [raceId, roomId, loadExistingPrediction])

  const getAvailableDrivers = useMemo(() => {
    return (position: number): Array<{ label: string; value: string; car: string }> => {
      const drivers = Array.isArray(race.drivers) ? race.drivers : []
      const cars = Array.isArray(race.cars) ? race.cars : []
      if (drivers.length === 0) return []
      const selectedDrivers = Object.values(positionToDriver)
      const currentDriver = positionToDriver[position]
      return drivers
        .map((driver, index) => ({
          label: driver,
          value: driver,
          car: cars[index] || 'Unknown',
        }))
        .filter(
          (driver) =>
            driver.value === currentDriver || !selectedDrivers.includes(driver.value)
        )
    }
  }, [race, positionToDriver])

  const handlePositionChange = (position: number, driver: string) => {
    if (driver === '') {
      const newPositionToDriver = { ...positionToDriver }
      delete newPositionToDriver[position]
      setPositionToDriver(newPositionToDriver)
    } else {
      const existingPosition = Object.entries(positionToDriver).find(
        ([pos, dr]) => dr === driver && Number(pos) !== position
      )
      const newPositionToDriver = { ...positionToDriver }
      if (existingPosition) {
        delete newPositionToDriver[Number(existingPosition[0])]
      }
      newPositionToDriver[position] = driver
      setPositionToDriver(newPositionToDriver)
    }
  }

  const handleSelectDriver = (driver: string) => {
    const currentPosition = selectedPositionIndex + 1
    handlePositionChange(currentPosition, driver)
    setSelectedPositionIndex((i) => Math.min(i + 1, POSITIONS - 1))
  }

  const handleSave = async () => {
    if (!roomId || !raceId) {
      toast.error('Missing room or race ID')
      return
    }
    const selectedPositions = Object.keys(positionToDriver).map(Number)
    const uniquePositions = new Set(selectedPositions)
    if (selectedPositions.length !== 10 || uniquePositions.size !== 10) {
      toast.error('Please select exactly 10 different positions (1-10)')
      return
    }
    const validPositions = selectedPositions.every((p) => p >= 1 && p <= 10)
    if (!validPositions) {
      toast.error('All positions must be between 1 and 10')
      return
    }
    const predictedPositions: Record<string, number> = {}
    Object.entries(positionToDriver).forEach(([position, driver]) => {
      predictedPositions[driver] = Number(position)
    })
    try {
      setIsSaving(true)
      if (isEditMode && prediction) {
        await formula1Service.updatePrediction(roomId, raceId, { predictedPositions })
        toast.success('Prediction updated successfully!')
      } else {
        await formula1Service.createPrediction({
          roomId,
          raceId,
          predictedPositions,
        })
        toast.success('Prediction saved successfully!')
        setIsEditMode(true)
      }
      await loadExistingPrediction()
      onSaved?.()
    } catch (error: any) {
      console.error('Error saving prediction:', error)
      toast.error(error.response?.data?.message || 'Failed to save prediction')
    } finally {
      setIsSaving(false)
    }
  }

  const drivers = Array.isArray(race.drivers) ? race.drivers : []
  const filledPositions = Object.keys(positionToDriver).length
  const currentPosition = selectedPositionIndex + 1
  const availableDrivers = getAvailableDrivers(currentPosition)

  if (drivers.length === 0) {
    return (
      <div className="blue-sky-card rounded-2xl p-4 sm:p-6">
        <p className="text-white/80">No drivers available for this race</p>
      </div>
    )
  }

  return (
    <div className="blue-sky-card rounded-2xl p-4 sm:p-5 md:p-6 mb-6 sm:mb-8 max-w-md mx-auto">
      <h2 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2 text-center">
        Make Your Prediction
      </h2>
      <p className="text-white/70 text-xs sm:text-sm mb-4 text-center">
        Predict positions 1–10 one by one. Select one driver per place.
      </p>

      {/* Progress: dots + step count */}
      <div className="flex items-center justify-between w-full mb-4">
        <div className="flex gap-1 flex-wrap">
          {Array.from({ length: POSITIONS }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 w-3 sm:w-4 rounded-sm flex-shrink-0 transition-colors ${
                i < selectedPositionIndex
                  ? 'bg-red-500'
                  : i === selectedPositionIndex
                    ? 'bg-red-400'
                    : 'bg-white/20'
              }`}
            />
          ))}
        </div>
        <span className="text-white/80 font-semibold text-xs sm:text-sm ml-2 shrink-0">
          {currentPosition}/{POSITIONS}
        </span>
      </div>

      {/* Position selector: prev [ "Nth place" ] next */}
      <div className="flex items-center justify-center gap-2 sm:gap-4 mb-4">
        <button
          type="button"
          onClick={() => setSelectedPositionIndex((i) => Math.max(0, i - 1))}
          disabled={selectedPositionIndex === 0}
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white touch-manipulation"
          aria-label="Previous position"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <div className="min-w-[120px] sm:min-w-[140px] text-center px-3 py-2 rounded-xl bg-white/10 border border-white/20">
          <span className="text-yellow-400 font-bold text-base sm:text-lg">
            {positionLabel(currentPosition)} place
          </span>
        </div>
        <button
          type="button"
          onClick={() => setSelectedPositionIndex((i) => Math.min(POSITIONS - 1, i + 1))}
          disabled={selectedPositionIndex >= POSITIONS - 1}
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white touch-manipulation"
          aria-label="Next position"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Current selection display */}
      {positionToDriver[currentPosition] && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-center">
          <p className="text-white/80 text-xs">Selected for {positionLabel(currentPosition)}</p>
          <p className="text-red-100 font-semibold text-sm truncate">{positionToDriver[currentPosition]}</p>
        </div>
      )}

      {/* Select one driver - scrollable list */}
      <p className="text-white/70 text-xs sm:text-sm mb-2">Select one driver</p>
      <div className="max-h-[min(50vh,320px)] overflow-y-auto rounded-xl border border-white/10 bg-white/5 pr-1">
        <div className="p-2 space-y-1.5">
          {availableDrivers.map((driver) => (
            <button
              key={driver.value}
              type="button"
              onClick={() => handleSelectDriver(driver.value)}
              disabled={isSaving}
              className="w-full text-left px-4 py-3 sm:py-3.5 rounded-xl bg-white/10 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 text-white font-medium text-sm sm:text-base transition-colors disabled:opacity-50 touch-manipulation min-h-[48px] sm:min-h-[52px] flex items-center justify-between gap-2"
            >
              <span className="truncate">{driver.label}</span>
              <span className="text-white/60 text-xs sm:text-sm shrink-0">({driver.car})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between mb-2">
          <p className="text-white/70 text-xs sm:text-sm">Progress</p>
          <p className="text-white font-semibold text-sm">{filledPositions}/10 positions filled</p>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(filledPositions / 10) * 100}%` }}
          />
        </div>
      </div>

      {/* Save */}
      <div className="mt-4 sm:mt-5">
        <button
          onClick={handleSave}
          disabled={isSaving || filledPositions !== 10}
          className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 sm:py-3.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:shadow-none touch-manipulation min-h-[48px]"
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Update Prediction' : 'Save Prediction'}
        </button>
      </div>
    </div>
  )
}

export default Formula1PredictionForm
